
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Alert } from '@mui/material';
import {
  Dashboard as OverviewIcon,
  Receipt as BillingIcon,
  BarChart as UsageIcon,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { useAccount } from '../../contexts/AccountContext';
import { useLoader } from '../../contexts/LoaderContext';
import { BillingAndInvoiceProvider, useBillingAndInvoice } from '../../contexts/BillingAndInvoiceContext';
import api from '../../services/api';
import RenewSubscriptionModal from '../../components/common/RenewSubscriptionModal';
import UpgradeToProButton from '../../components/common/UpgradeToProButton';
import UpgradeToProModal from '../../components/common/UpgradeToProModal';
import AddUserSeatsButton from './AddUserSeatsButton';
import AddCreditsButton from './AddCreditsButton';
import ProfarmaInvoiceModalAddUser from './ProfarmaInvoiceModalAddUser';
import ProfarmaInvoiceModalAddCredits from './ProfarmaInvoiceModalAddCredits';
import RazorPayPaymentModal, { PAYMENT_STATUS } from '../../components/common/RazorPayPaymentModal';
import {
  parseHistoryDate,
  getDateRange,
} from './subscription/subscriptionData';
import SubscriptionOverview from './subscription/SubscriptionOverview';
import SubscriptionUsage from './subscription/SubscriptionUsage';
import SubscriptionBilling from './subscription/SubscriptionBilling';

const TAB_VALUES_PAID = ['overview', 'billing', 'usage'];
const TAB_VALUES_FREE = ['overview', 'billing', 'usage'];

/** Voice credits usage API: returns credits_distribution (chart) and credit_transaction (table). */
const VOICE_CREDITS_USAGE_API = 'subscription/voice-credits/usage/';
const getPlanType = (cp) => {
  return cp?.plan?.plan_type || cp?.plan_type || 'free';
};

/** Poll invoice status while Add User payment modal is open (Razorpay may redirect to ngrok; we detect success via API). */
const ADD_USER_POLL_INTERVAL_MS = 2500;
const ADD_USER_POLL_MAX_ATTEMPTS = 120; // ~5 min

function SubscriptionContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchSubscriptionData, subscriptionData, setSubscriptionData, currentPlan, setCurrentPlan, activeAgents } = useAccount();
  const { getProformaUnits, getProformaPreview, confirmBilling, getInvoicePaymentStatus } = useBillingAndInvoice();
  const [userSlotUsage, setUserSlotUsage] = useState({ used: 0, total: 0, percentage: 0 });
  const [creditsUsage, setCreditsUsage] = useState({ used: 0, total: 0, remaining: 0, percentage: 0 });
  const [error, setError] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [walletHistory, setWalletHistory] = useState([]);
  const [usageTransactions, setUsageTransactions] = useState([]);
  const [usageCreditsDistribution, setUsageCreditsDistribution] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('week');
  const [customStart, setCustomStart] = useState(() => format(subDays(new Date(), 6), 'yyyy-MM-dd'));
  const [customEnd, setCustomEnd] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const { showLoader, hideLoader } = useLoader();
  
  // Proforma invoice modals (separate per action: upgrade | add_user | add_credits)
  const [showProfarmaModal, setShowProfarmaModal] = useState(false);
  const [profarmaAction, setProfarmaAction] = useState(null); // 'upgrade' | 'add_user' | 'add_credits'
  const [profarmaData, setProfarmaData] = useState(null);
  const [profarmaLoading, setProfarmaLoading] = useState(false);
  const [profarmaError, setProfarmaError] = useState(null);
  const [profarmaPaymentUrl, setProfarmaPaymentUrl] = useState(null); // set after Confirm API success
  const [profarmaConfirmLoading, setProfarmaConfirmLoading] = useState(false);
  const [profarmaConfirmError, setProfarmaConfirmError] = useState(null);
  const [razorPayAddUserOpen, setRazorPayAddUserOpen] = useState(false);
  const [addUserPaymentStatus, setAddUserPaymentStatus] = useState(PAYMENT_STATUS.IDLE);
  const [addUserInvoiceNumber, setAddUserInvoiceNumber] = useState(null); // set after confirm (add_user) for status polling
  const [addUserRefreshStatusLoading, setAddUserRefreshStatusLoading] = useState(false);
  const [addUserUnitsData, setAddUserUnitsData] = useState(null); // from proforma-units API; used for Step 1 and FE calculations
  const [addCreditsUnitsData, setAddCreditsUnitsData] = useState(null); // from proforma-units API; used for Step 1 and FE calculations
  const [razorPayAddCreditsOpen, setRazorPayAddCreditsOpen] = useState(false);
  const [addCreditsPaymentStatus, setAddCreditsPaymentStatus] = useState(PAYMENT_STATUS.IDLE);
  const [addCreditsInvoiceNumber, setAddCreditsInvoiceNumber] = useState(null);
  const [addCreditsRefreshStatusLoading, setAddCreditsRefreshStatusLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(getPlanType(currentPlan) === 'free');
  const TAB_VALUES = isFreePlan ? TAB_VALUES_FREE : TAB_VALUES_PAID;
  const tabParam = searchParams.get('tab');
  const tabIndex = TAB_VALUES.includes(tabParam) ? TAB_VALUES.indexOf(tabParam) : 0;

  const setTab = (index) => {
    const value = TAB_VALUES[index];
    if (value === 'overview') {
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    } else {
      setSearchParams({ ...Object.fromEntries(searchParams), tab: value }, { replace: true });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (tabIndex === 2) {
      fetchUsage();
    }
  }, [tabIndex, dateFilter, customStart, customEnd]);


  // Open upgrade modal when navigated with ?openUpgrade=1
  useEffect(() => {
    const openUpgrade = searchParams.get('openUpgrade');
    if (openUpgrade === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('openUpgrade');
      setSearchParams(next, { replace: true });
      setShowUpgradeModal(true);
    }
  }, [searchParams]);

  // Open add-user modal (step 1: Configure) when navigated from Header with ?openAddUser=1; fetch proforma-units only
  useEffect(() => {
    const openAddUser = searchParams.get('openAddUser');
    if (openAddUser === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('openAddUser');
      setSearchParams(next, { replace: true });
      fetchAddUserUnits(1);
    }
  }, [searchParams]);

  // Open add-credits modal (units only) when navigated from Header with ?openAddCredits=1
  useEffect(() => {
    const openAddCredits = searchParams.get('openAddCredits');
    if (openAddCredits === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('openAddCredits');
      setSearchParams(next, { replace: true });
      fetchAddCreditsUnits();
    }
  }, [searchParams]);

  // Usage tab: single API returns credits_distribution (chart) and credit_transaction (table); default last 7 days
  const fetchUsage = async () => {
    setUsageLoading(true);
    try {
      const { start, end } = getDateRange(dateFilter, customStart, customEnd);
      const params = {
        start_date: format(start, 'yyyy-MM-dd'),
        end_date: format(end, 'yyyy-MM-dd'),
      };
      const res = await api.get(VOICE_CREDITS_USAGE_API, { params });
      const data = res?.data || {};
      const creditTransaction = data.credit_transaction;
      const creditsDistribution = data.credits_distribution;
      setUsageTransactions(Array.isArray(creditTransaction) ? creditTransaction : []);
      setUsageCreditsDistribution(Array.isArray(creditsDistribution) ? creditsDistribution : []);
    } catch (_) {
      setUsageTransactions([]);
      setUsageCreditsDistribution([]);
    } finally {
      setUsageLoading(false);
    }
  };

  // Spendings tab: subscription/voice-credits/spendings/
  const fetchSpendings = async () => {
    setWalletLoading(true);
    try {
      const res = await api.get('subscription/voice-credits/spendings/');
      const list = res?.data?.data ?? (Array.isArray(res?.data) ? res.data : []);
      setWalletHistory(Array.isArray(list) ? list : []);
    } catch (_) {
      setWalletHistory([]);
    } finally {
      setWalletLoading(false);
    }
  };

  const filteredUsageTransactions = useMemo(() => {
    const { start, end } = getDateRange(dateFilter, customStart, customEnd);
    return usageTransactions.filter((row) => {
      const d = parseHistoryDate(row.date_time || row.created_at);
      return d && d >= start && d <= end;
    });
  }, [usageTransactions, dateFilter, customStart, customEnd]);

  const voiceCreditsBarData = useMemo(() => {
    const { start, end } = getDateRange(dateFilter, customStart, customEnd);
    if (usageCreditsDistribution?.length > 0) {
      return usageCreditsDistribution;
    }
    const days = [];
    let cursor = new Date(start);
    while (cursor <= end) {
      const dayStr = format(cursor, 'MMM d');
      const used = filteredUsageTransactions
        .filter((r) => {
          const d = parseHistoryDate(r.date_time || r.created_at);
          return d && format(d, 'yyyy-MM-dd') === format(cursor, 'yyyy-MM-dd');
        })
        .reduce((sum, r) => sum + (r.event_type === 'CU' ? (r.credit_used ?? r.voice_credits ?? 0) : 0), 0);
      days.push({ period: dayStr, used });
      cursor.setDate(cursor.getDate() + 1);
    }
    const hasAny = days.some((d) => d.used > 0);
    return hasAny ? days : [];
  }, [usageCreditsDistribution, filteredUsageTransactions, dateFilter, customStart, customEnd]);

  const filteredWalletHistory = useMemo(() => {
    const { start, end } = getDateRange(dateFilter, customStart, customEnd);
    return walletHistory.filter((row) => {
      const d = parseHistoryDate(row.created_at);
      return d && d >= start && d <= end;
    });
  }, [walletHistory, dateFilter, customStart, customEnd]);

  const userWiseBarData = useMemo(() => {
    if (!activeAgents?.agents?.length) return [];
    const withCredits = activeAgents.agents.slice(0, 10).map((a) => ({
      name: a.name || a.email || 'User',
      credits: a.voice_credits ?? 0,
    }));
    return withCredits.some((r) => r.credits > 0) ? withCredits : [];
  }, [activeAgents]);

  const showUserWisePlaceholder = userWiseBarData.length && userWiseBarData[0]?.name === 'No user data';

  const fetchData = async () => {
    try {
      showLoader();
      setError(null);
      const response_data = await fetchSubscriptionData();
      console.log('fetchData response_data:', response_data);
      if (response_data) {
        setSubscriptionData(response_data);
        setCurrentPlan(response_data);
        setIsFreePlan(getPlanType(response_data) === 'free');
        
        // Set users data
        const users = response_data.users ?? {};
        const totalSeats = users.total_seats ?? 0;
        const activeUsers = users.active ?? 0;
        setUserSlotUsage({
          used: activeUsers,
          total: totalSeats,
          percentage: users.perc_used ?? (totalSeats > 0 ? (activeUsers / totalSeats) * 100 : 0),
        });

        // Set credits data
        const credits = response_data?.credits ?? {};
        const totalCredits = credits.total ?? 0;
        const used = credits.used ?? 0;
        const remaining = credits.available ?? (totalCredits - used);
        setCreditsUsage({
          used,
          total: totalCredits,
          remaining: remaining >= 0 ? remaining : 0,
          percentage: totalCredits > 0 ? (used / totalCredits) * 100 : 0,
        });
      }

      // Optional: fetch additional usage data if needed
      try {
        const usageRes = await api.get('subscription/usage/');
        const u = usageRes?.data || {};
        if (u.user_slots_used != null || u.user_slots_total != null) {
          const used = u.user_slots_used ?? 0;
          const total = u.user_slots_total;
          setUserSlotUsage((prev) => ({
            ...prev,
            used,
            total: total ?? prev.total,
            percentage: total != null && total > 0 ? (used / total) * 100 : prev.percentage,
          }));
        }
        if (u.voice_credits_available != null || u.total_voice_credits_assigned != null) {
          const remaining = u.voice_credits_available ?? 0;
          const total = u.total_voice_credits_assigned;
          setCreditsUsage((prev) => ({
            ...prev,
            remaining,
            total: total ?? prev.total,
            used: total != null ? Math.max(0, total - remaining) : prev.used,
            percentage: total != null && total > 0 ? ((total - remaining) / total) * 100 : prev.percentage,
          }));
        }
      } catch (_) {}
    } catch (err) {
      setError('Failed to load subscription data. Please try again later. Error: ' + err.message);
      console.error('Error fetching subscription data:', err);
    } finally {
      hideLoader();
    }
  };


  // Fetch pricing units for add_user (Step 1). GET /billing/pricing-units/add_user/
  const fetchAddUserUnits = async (seats = 1) => {
    setProfarmaAction('add_user');
    setShowProfarmaModal(true);
    setProfarmaLoading(true);
    setProfarmaError(null);
    setProfarmaData(null);
    setProfarmaPaymentUrl(null);
    setProfarmaConfirmError(null);
    setAddUserUnitsData(null);
    try {
      const response = await getProformaUnits('add_user');
      if (response?.success && response?.units != null) {
        setAddUserUnitsData(response);
      } else {
        setProfarmaError(response?.error || 'Failed to load add-user data');
      }
    } catch (err) {
      console.error('Error fetching pricing units (add_user):', err);
      setProfarmaError(err.response?.data?.error || err.message || 'Failed to load add-user data');
    } finally {
      setProfarmaLoading(false);
    }
  };


  // Fetch pricing units for add_user (Step 1). GET /billing/pricing-units/add_user/
  const fetchAddCreditsUnits = async (seats = 1) => {
    setProfarmaAction('add_credits');
    setShowProfarmaModal(true);
    setProfarmaLoading(true);
    setProfarmaError(null);
    setProfarmaData(null);
    setProfarmaPaymentUrl(null);
    setProfarmaConfirmError(null);
    setAddCreditsUnitsData(null);
    try {
      const response = await getProformaUnits('add_credits');
      if (response?.success && (response?.units != null || response?.data != null)) {
        setAddCreditsUnitsData(response);
      } else {
        setProfarmaError(response?.error || 'Failed to load add-credits data');
      }
    } catch (err) {
      console.error('Error fetching pricing units (add_credits):', err);
      setProfarmaError(err.response?.data?.error || err.message || 'Failed to load add-credits data');
    } finally {
      setProfarmaLoading(false);
    }
  };

  // Fetch proforma invoice preview (Step 2). POST /billing/proforma/ with body { action, seats|credits|... }.
  const fetchProfarmaInvoice = async (action, params = {}, options = {}) => {
    setProfarmaAction(action);
    if (action !== 'add_user') {
      setShowProfarmaModal(true);
      setProfarmaData(null);
    }
    setProfarmaLoading(true);
    setProfarmaError(null);
    setProfarmaPaymentUrl(null);
    setProfarmaConfirmError(null);
    if (!options.onSuccess && action !== 'add_user') {
      setProfarmaData(null);
    }
    const body = { action };
    if (action === 'add_user') body.seats = params.slots ?? params.seats ?? 1;
    if (action === 'add_credits') body.credits = params.credits ?? 1000;
    if (action === 'upgrade') {
      body.slots = params.slots;
      body.billing_cycle = params.billing_cycle;
      body.credits = params.credits;
      if (params.plan_id != null) body.plan_id = params.plan_id;
    }
    let successCallback = null;
    try {
      const response = await getProformaPreview(body);
      const preview = response?.preview ?? response?.data;
      if (response?.success && preview) {
        setProfarmaData(preview);
        successCallback = options.onSuccess;
      } else {
        setProfarmaError(response?.error || 'Failed to load invoice preview');
      }
    } catch (err) {
      console.error('Error fetching proforma invoice:', err);
      setProfarmaError(err.response?.data?.error || err.message || 'Failed to load invoice preview');
    } finally {
      setProfarmaLoading(false);
      if (successCallback) {
        setTimeout(successCallback, 0);
      }
    }
  };

  // Handle Add Users — open modal (step 1); call proforma-units only; FE does all calculations from that data
  const handleAddUsersClick = async () => {
    await fetchAddUserUnits(1);
  };

  // Handle Add Credits — open modal (step 1); call proforma-units only; FE does all calculations from that data
  const handleAddCreditsClick = async () => {
    await fetchAddCreditsUnits(1);
  };

  // Continue to Review: fetch proforma preview (line items, subtotal, tax, total) for Step 2. Backend expects 'seats'.
  const handleAddUserConfirm = (slots, onSuccess) => {
    if (slots >= 1) {
      fetchProfarmaInvoice('add_user', { slots, seats: slots }, { onSuccess });
    }
  };

  const handleAddUserBack = () => {
    setProfarmaData(null);
  };

  // Handle Top-up Credits — open modal with pricing units only (like Add User); user configures credits then Continue to Review
  const handleTopUpCreditsClick = async () => {
    await fetchAddCreditsUnits();
  };

  const handleAddCreditsChange = (credits, onSuccess) => {
    if (credits >= 1000) {
      fetchProfarmaInvoice('add_credits', { credits }, { onSuccess });
    }
  };

  // Confirm: POST /billing/confirm/ — creates invoice and returns payment_link (same API as upgrade).
  const handleConfirm = async () => {
    if (!profarmaData || !profarmaAction) return;
    setProfarmaConfirmLoading(true);
    setProfarmaConfirmError(null);
    try {
      const lineItems = profarmaData.particulars || profarmaData.line_items || [];
      let payload = { action: profarmaAction };
      if (profarmaAction === 'add_user') {
        payload.slots = lineItems[0]?.quantity ?? 1;
      } else if (profarmaAction === 'add_credits') {
        payload.credits = lineItems[0]?.quantity ?? 1000;
      } else if (profarmaAction === 'upgrade') {
        payload = { ...profarmaData.action_params, action: 'upgrade' };
        if (payload.total_user == null) payload.total_user = profarmaData.slots ?? lineItems[0]?.quantity ?? 1;
        if (payload.billing_cycle == null) payload.billing_cycle = 3;
        if (payload.additional_credits == null) payload.additional_credits = 0;
        if (payload.plan_id == null && profarmaData.plan_id != null) payload.plan_id = profarmaData.plan_id;
      }
      const res = await confirmBilling(payload);
      const paymentUrl = res?.invoice?.payment_link ?? res?.payment_url;
      if (res?.success && paymentUrl) {
        setProfarmaPaymentUrl(paymentUrl);
        if (profarmaAction === 'add_user') {
          setAddUserInvoiceNumber(res?.invoice?.invoice_number ?? res?.invoice_number ?? null);
        } else if (profarmaAction === 'add_credits') {
          setAddCreditsInvoiceNumber(res?.invoice?.invoice_number ?? res?.invoice_number ?? null);
        }
      } else {
        setProfarmaConfirmError(res?.error || res?.detail || 'Failed to create payment link');
      }
    } catch (err) {
      setProfarmaConfirmError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to create payment link');
    } finally {
      setProfarmaConfirmLoading(false);
    }
  };

  // Pay Now: add_user / add_credits → open Razorpay modal with payment link; both use success/retry modal with polling.
  const handlePayNow = () => {
    const url = profarmaPaymentUrl || profarmaData?.payment_link;
    if (!url) return;
    if (profarmaAction === 'add_user') {
      setAddUserPaymentStatus(PAYMENT_STATUS.PENDING);
      setRazorPayAddUserOpen(true);
      setShowProfarmaModal(false);
    } else if (profarmaAction === 'add_credits') {
      setAddCreditsPaymentStatus(PAYMENT_STATUS.PENDING);
      setRazorPayAddCreditsOpen(true);
      setShowProfarmaModal(false);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
      handleCloseProfarmaModal();
      setProfarmaAction(null);
    }
  };

  const handleRazorPayAddUserClose = () => {
    setRazorPayAddUserOpen(false);
    setAddUserPaymentStatus(PAYMENT_STATUS.IDLE);
    setAddUserInvoiceNumber(null);
    setProfarmaPaymentUrl(null);
    setProfarmaAction(null);
    setProfarmaData(null);
    setAddUserUnitsData(null);
    fetchData();
  };

  // When user closes modal while payment still pending → show failed state (Retry / Change method) like upgrade flow
  const handleRazorPayAddUserCloseRequest = () => {
    if (addUserPaymentStatus === PAYMENT_STATUS.PENDING) {
      setAddUserPaymentStatus(PAYMENT_STATUS.FAILED);
      return;
    }
    handleRazorPayAddUserClose();
  };

  const handleAddUserRefreshStatus = async () => {
    if (!addUserInvoiceNumber) return;
    setAddUserRefreshStatusLoading(true);
    try {
      const res = await getInvoicePaymentStatus(addUserInvoiceNumber);
      if (res?.success && res?.paid) setAddUserPaymentStatus(PAYMENT_STATUS.SUCCESS);
    } finally {
      setAddUserRefreshStatusLoading(false);
    }
  };

  const handleAddUserRetry = () => {
    setAddUserPaymentStatus(PAYMENT_STATUS.PENDING);
  };

  // Auto-poll invoice status while Add User payment modal is open (payment may complete in iframe/redirect to ngrok)
  useEffect(() => {
    if (!razorPayAddUserOpen || !addUserInvoiceNumber || addUserPaymentStatus !== PAYMENT_STATUS.PENDING) return;
    let cancelled = false;
    let attempts = 0;
    const timerRef = { current: null };
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > ADD_USER_POLL_MAX_ATTEMPTS) return;
      const res = await getInvoicePaymentStatus(addUserInvoiceNumber);
      if (cancelled) return;
      if (res?.success && res?.paid) {
        setAddUserPaymentStatus(PAYMENT_STATUS.SUCCESS);
        return;
      }
      if (!cancelled) timerRef.current = setTimeout(poll, ADD_USER_POLL_INTERVAL_MS);
    };
    timerRef.current = setTimeout(poll, ADD_USER_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [razorPayAddUserOpen, addUserInvoiceNumber, addUserPaymentStatus, getInvoicePaymentStatus]);

  const handleRazorPayAddCreditsClose = () => {
    setRazorPayAddCreditsOpen(false);
    setAddCreditsPaymentStatus(PAYMENT_STATUS.IDLE);
    setAddCreditsInvoiceNumber(null);
    setProfarmaPaymentUrl(null);
    setProfarmaAction(null);
    setProfarmaData(null);
    setAddCreditsUnitsData(null);
    fetchData();
  };

  const handleRazorPayAddCreditsCloseRequest = () => {
    if (addCreditsPaymentStatus === PAYMENT_STATUS.PENDING) {
      setAddCreditsPaymentStatus(PAYMENT_STATUS.FAILED);
      return;
    }
    handleRazorPayAddCreditsClose();
  };

  const handleAddCreditsRefreshStatus = async () => {
    if (!addCreditsInvoiceNumber) return;
    setAddCreditsRefreshStatusLoading(true);
    try {
      const res = await getInvoicePaymentStatus(addCreditsInvoiceNumber);
      if (res?.success && res?.paid) setAddCreditsPaymentStatus(PAYMENT_STATUS.SUCCESS);
    } finally {
      setAddCreditsRefreshStatusLoading(false);
    }
  };

  const handleAddCreditsRetry = () => {
    setAddCreditsPaymentStatus(PAYMENT_STATUS.PENDING);
  };

  // Auto-poll invoice status while Add Credits payment modal is open
  useEffect(() => {
    if (!razorPayAddCreditsOpen || !addCreditsInvoiceNumber || addCreditsPaymentStatus !== PAYMENT_STATUS.PENDING) return;
    let cancelled = false;
    let attempts = 0;
    const timerRef = { current: null };
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > ADD_USER_POLL_MAX_ATTEMPTS) return;
      const res = await getInvoicePaymentStatus(addCreditsInvoiceNumber);
      if (cancelled) return;
      if (res?.success && res?.paid) setAddCreditsPaymentStatus(PAYMENT_STATUS.SUCCESS);
      if (!cancelled) timerRef.current = setTimeout(poll, ADD_USER_POLL_INTERVAL_MS);
    };
    timerRef.current = setTimeout(poll, ADD_USER_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [razorPayAddCreditsOpen, addCreditsInvoiceNumber, addCreditsPaymentStatus, getInvoicePaymentStatus]);

  const handleCloseProfarmaModal = () => {
    setShowProfarmaModal(false);
    setProfarmaPaymentUrl(null);
    setProfarmaConfirmError(null);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" sx={{ mt: 2, display: 'block', mx: 'auto' }} onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }

  if (!currentPlan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No subscription data available. Please contact support if you believe this is an error.</Alert>
      </Box>
    );
  }

  const plan = currentPlan?.plan ?? {};
  const isExpired = typeof plan.days_remaining === 'number' && plan.days_remaining <= 0;
  const isPaidPlan = plan.plan_type && plan.plan_type !== 'free';
  const canAddUserOrTopUp = isPaidPlan && !isExpired;

  const SIDEBAR_NAV = [
    { id: 'overview', label: 'Overview', icon: OverviewIcon },
    { id: 'billing', label: 'Billing & Invoice', icon: BillingIcon },
    { id: 'usage', label: 'Usage', icon: UsageIcon },
  ];

  // Teal palette for sidebar and buttons (match SubscriptionOverview)
  const teal = { 50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59' };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom fontFamily="'Plus Jakarta Sans', sans-serif">
            Subscription, Billing and Usage
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Show Add Users and Top-up Credits ONLY for Pro/Paid plans — User: dark teal border, Credits: filled teal */}
          {isPaidPlan && (
            <>
              <AddUserSeatsButton
                onClick={handleAddUsersClick}
                disabled={!canAddUserOrTopUp}
                sx={{
                  borderColor: teal[700],
                  color: teal[800],
                  '&:hover': { borderColor: teal[600], bgcolor: teal[50], color: teal[800] },
                }}
              />
              <AddCreditsButton
                variant="contained"
                onClick={handleTopUpCreditsClick}
                disabled={!canAddUserOrTopUp}
                sx={{
                  bgcolor: teal[600],
                  color: '#fff',
                  '&:hover': { bgcolor: teal[700] },
                }}
              />
            </>
          )}
          
          {/* Show Upgrade Button ONLY for Free plan */}
          {isFreePlan && !isPaidPlan && (
            <>
              <UpgradeToProButton onClick={() => setShowUpgradeModal(true)} />
              <UpgradeToProModal open={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0, alignItems: 'stretch', mt: 1 }}>
        <Box
          sx={{
            minWidth: 220,
            flexShrink: 0,
            borderRight: 1,
            borderColor: teal[200],
            py: 0.5,
            px: 0.5,
          }}
        >
          {SIDEBAR_NAV.map((item, idx) => {
            const isActive = tabIndex === idx;
            const Icon = item.icon;
            return (
              <Box
                key={item.id}
                component="button"
                type="button"
                onClick={() => setTab(idx)}
                aria-current={isActive ? 'page' : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.75, sm: 1, md: 1.25 },
                  width: '100%',
                  px: 2,
                  py: { xs: 0.25, sm: 0.5 },
                  border: 'none',
                  borderRadius: 1.5,
                  mx: 0.5,
                  mb: 0.25,
                  minHeight: { xs: 28, sm: 32, md: 36 },
                  bgcolor: isActive ? teal[50] : 'transparent',
                  color: isActive ? teal[800] : '#71717a',
                  fontWeight: isActive ? 600 : 500,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  position: 'relative',
                  ...(isActive && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '3px',
                      bgcolor: teal[600],
                      borderTopLeftRadius: 3,
                      borderBottomLeftRadius: 3,
                    },
                  }),
                  '&:hover': {
                    bgcolor: teal[50],
                    color: teal[800],
                  },
                }}
              >
                <Icon sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' } }} />
                {item.label}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pl: 3, pt: 0 }}>
      <Box role="tabpanel" hidden={tabIndex !== 0} id="subscription-tabpanel-0" aria-labelledby="subscription-tab-0">
        {tabIndex === 0 && (
          <SubscriptionOverview
            subscriptionData={currentPlan}
            userSlotUsage={userSlotUsage}
            creditsUsage={creditsUsage}
            hasVoiceCredits={!isFreePlan}
          />
        )}
      </Box>

      <Box role="tabpanel" hidden={tabIndex !== 1} id="subscription-tabpanel-1" aria-labelledby="subscription-tab-1">
        {tabIndex === 1 && <SubscriptionBilling />}
      </Box>

      <Box role="tabpanel" hidden={tabIndex !== 2} id="subscription-tabpanel-2" aria-labelledby="subscription-tab-2">
        {tabIndex === 2 && (
          <SubscriptionUsage
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            customStart={customStart}
            setCustomStart={setCustomStart}
            customEnd={customEnd}
            setCustomEnd={setCustomEnd}
            usageLoading={usageLoading}
            voiceCreditsBarData={voiceCreditsBarData}
            userWiseBarData={userWiseBarData}
            filteredUsageTransactions={filteredUsageTransactions}
            showUserWisePlaceholder={showUserWisePlaceholder}
            hasVoiceCredits={!isFreePlan}
          />
        )}
      </Box>

        </Box>
      </Box>

      <RenewSubscriptionModal open={showRenewModal} onClose={() => setShowRenewModal(false)} currentPlan={currentPlan} fetchSubscriptionData={fetchSubscriptionData} />

      {/* Proforma Invoice Modals — add_user and add_credits only; upgrade uses UpgradeToProModal */}
      <ProfarmaInvoiceModalAddUser
        open={showProfarmaModal && profarmaAction === 'add_user'}
        onClose={() => { handleCloseProfarmaModal(); setProfarmaAction(null); setAddUserUnitsData(null); }}
        unitsData={addUserUnitsData}
        data={profarmaData}
        loading={profarmaLoading}
        error={profarmaError}
        paymentUrl={profarmaPaymentUrl}
        onConfirm={handleConfirm}
        confirmLoading={profarmaConfirmLoading}
        confirmError={profarmaConfirmError}
        onPayNow={handlePayNow}
        onNext={handleAddUserConfirm}
        onBack={handleAddUserBack}
      />
      <ProfarmaInvoiceModalAddCredits
        open={showProfarmaModal && profarmaAction === 'add_credits'}
        onClose={() => { handleCloseProfarmaModal(); setProfarmaAction(null); setAddCreditsUnitsData(null); }}
        unitsData={addCreditsUnitsData}
        data={profarmaData}
        loading={profarmaLoading}
        error={profarmaError}
        paymentUrl={profarmaPaymentUrl}
        onConfirm={handleConfirm}
        confirmLoading={profarmaConfirmLoading}
        confirmError={profarmaConfirmError}
        onPayNow={handlePayNow}
        onCreditsChange={handleAddCreditsChange}
        onBack={() => setProfarmaData(null)}
      />

      <RazorPayPaymentModal
        open={razorPayAddUserOpen && !!profarmaPaymentUrl}
        onClose={handleRazorPayAddUserCloseRequest}
        paymentUrl={profarmaPaymentUrl}
        title="Complete payment — Add users"
        paymentStatus={addUserPaymentStatus}
        totalPayable={profarmaData?.total != null ? `₹${Number(profarmaData.total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : undefined}
        onRetry={handleAddUserRetry}
        onRefreshStatus={handleAddUserRefreshStatus}
        onGoToSubscription={handleRazorPayAddUserClose}
        onChangeMethod={handleRazorPayAddUserClose}
        isRefreshingStatus={addUserRefreshStatusLoading}
        successTitle="Users added successfully"
        successMessage="Reloading your account…"
      />

      <RazorPayPaymentModal
        open={razorPayAddCreditsOpen && !!profarmaPaymentUrl}
        onClose={handleRazorPayAddCreditsCloseRequest}
        paymentUrl={profarmaPaymentUrl}
        title="Complete payment — Top up credits"
        paymentStatus={addCreditsPaymentStatus}
        totalPayable={profarmaData?.total != null ? `₹${Number(profarmaData.total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : undefined}
        onRetry={handleAddCreditsRetry}
        onRefreshStatus={handleAddCreditsRefreshStatus}
        onGoToSubscription={handleRazorPayAddCreditsClose}
        onChangeMethod={handleRazorPayAddCreditsClose}
        isRefreshingStatus={addCreditsRefreshStatusLoading}
        successTitle="Credits added successfully"
        successMessage="Reloading your account…"
      />
    </Box>
  );
}

export default function Subscription() {
  return (
    <BillingAndInvoiceProvider>
      <SubscriptionContent />
    </BillingAndInvoiceProvider>
  );
}